import React, { useMemo, useState } from "react";

import {
  faChevronDown,
  faCog,
  faPlus,
  faRefresh,
} from "@fortawesome/pro-regular-svg-icons";
import { useQueryClient } from "@tanstack/react-query";
import { createColumnHelper, SortingState } from "@tanstack/react-table";
import { isEqual } from "lodash-es";
import { Link } from "react-router-dom";
import { twJoin } from "tailwind-merge";

import { CreateOperationModal } from "src/adminPanel/modulesV2/CreateOperationModal";
import { EnableModuleModal } from "src/adminPanel/modulesV2/EnableModuleModal";
import { LifecycleTransitionModal } from "src/adminPanel/modulesV2/LifecycleTransitionModal";
import { ModuleOperationsDetail } from "src/adminPanel/modulesV2/ModuleOperationsDetail";
import {
  ModuleVersionBars,
  VersionFilter,
} from "src/adminPanel/modulesV2/ModuleVersionBars";
import { SpecDiffModal } from "src/adminPanel/modulesV2/SpecDiffModal";
import {
  computeAvailableActions,
  enrichModulesForSearch,
  getApiErrorMessage,
  getModuleOperationStatus,
  getOperationStatusLabel,
  getOperationStatusPillVariant,
  LIFECYCLE_LABEL,
  LIFECYCLE_ORDER,
  LIFECYCLE_VARIANT,
  MODULE_SEARCH_OPTIONS,
  moduleHasOperationMatch,
  OPERATION_STATUS_FILTER_OPTIONS,
} from "src/adminPanel/modulesV2/utils";
import { SortableFilterHeader } from "src/adminPanel/SortableFilterHeader";
import { Module, moduleKeys, useCreateOperation } from "src/api/modulesApi";
import { Button } from "src/base-components/Button";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Pill } from "src/base-components/Pill";
import { CustomPopover } from "src/base-components/Popover";
import { Cell, TableComp } from "src/base-components/Table";
import {
  FlowApiSchemasModelUsageFeatureOverride,
  LifecycleState,
  ModuleKind,
  ModuleOperationStatus,
  WorkspaceConfig,
  WorkspaceDataplane,
} from "src/clients/flow-api";
import { ObjectDetailPane } from "src/dataTable/ObjectDetailPane";
import { Icon } from "src/design-system/Icon";
import { Modal, useModal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { useFuseSearch } from "src/utils/useFuseSearch";

type Props = {
  modules: Module[];
  searchTerm: string;
  workspaceMap: Map<string, { name: string; orgId: string; slug: string }>;
  orgMap: Map<string, string>;
  moduleKind: ModuleKind;
  workspaces: WorkspaceDataplane[];
  agentWorkspaceIds: Set<string>;
  // Workspaces that already have a module of this kind, computed from the full (unfiltered) list so
  // a hidden inactive module still blocks re-enabling its workspace.
  existingWorkspaceIds: Set<string>;
};

const columnHelper = createColumnHelper<Module>();

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const ObjectPreview: React.FC<{ value: Record<string, unknown> }> = ({
  value,
}) => {
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <CustomPopover
        button={
          <Pill size="sm" variant="gray">
            <Pill.Text fontType="code">{"{…}"}</Pill.Text>
          </Pill>
        }
        className="p-3"
        placement="bottom-start"
      >
        <ObjectDetailPane
          defaultInspectDepth={2}
          innerDimensionClass="max-h-80 max-w-md"
          jsonObject={value as Record<string, unknown>}
          speakPython={false}
        />
      </CustomPopover>
    </span>
  );
};

const ConfigValueCell: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === undefined || value === null) {
    return (
      <span className="font-inter-normal-13px text-gray-400 italic">—</span>
    );
  }
  if (typeof value === "boolean") {
    return (
      <Pill variant={value ? "green" : "red"}>
        <Pill.Text fontType="text">{String(value)}</Pill.Text>
      </Pill>
    );
  }
  if (typeof value === "object") {
    return <ObjectPreview value={value as Record<string, unknown>} />;
  }
  return (
    <span className="font-inter-normal-13px text-gray-900">
      {String(value)}
    </span>
  );
};

const getSpecStatus = (
  mod: Module,
): { variant: "green" | "yellow"; label: string } => {
  if (mod.spec === null) {
    return { variant: "yellow", label: "Pending" };
  }
  if (isEqual(mod.spec, mod.desiredSpec)) {
    return { variant: "green", label: "In Sync" };
  }
  return { variant: "yellow", label: "Updating" };
};

const LIFECYCLE_FILTER_OPTIONS = Object.values(LifecycleState).map((state) => ({
  value: state,
  label: LIFECYCLE_LABEL[state],
}));

const COPILOT_QUOTA_FILTER_OPTIONS = [
  { value: "enabled", label: "Enabled" },
  { value: "disabled", label: "Disabled" },
];

const isCaseCopilotQuotaEnabled = (ws: WorkspaceDataplane): boolean => {
  const config = (ws.config ?? {}) as WorkspaceConfig;
  const featureOverrides = (config.model_usage_feature_overrides ??
    {}) as Record<string, FlowApiSchemasModelUsageFeatureOverride>;
  return featureOverrides.case_copilot?.enabled ?? false;
};

const InFlightDot: React.FC = () => (
  <span
    aria-label="Converging to desired state"
    className="relative mr-1.5 inline-flex h-2 w-2"
  >
    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-yellow-400 opacity-75" />
    <span className="relative inline-flex h-2 w-2 rounded-full bg-yellow-500" />
  </span>
);

const profileLevelVariant = (
  level: number,
): "red" | "green" | "yellow" | "orange" | "indigo" =>
  level < 0
    ? "red"
    : level === 0
      ? "green"
      : level === 1
        ? "yellow"
        : level === 2
          ? "orange"
          : "indigo";

// Renders the "↳ WS: …" line nested beneath a module pill, with the workspace's value shown as
// its own coloured pill so both absolute states read at a glance.
const WorkspaceNestedLine: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="flex items-center gap-x-1 pl-0.5 font-inter-normal-12px text-gray-400">
    <span aria-hidden className="text-gray-300">
      ↳
    </span>
    <span>WS:</span>
    {children}
  </div>
);

// Shows the module's workspace lifecycle state nested beneath the module pill, as a coloured pill.
const WorkspaceLifecycleLine: React.FC<{
  workspaceState: LifecycleState | undefined;
}> = ({ workspaceState }) => {
  if (workspaceState === undefined) {
    return null;
  }
  return (
    <WorkspaceNestedLine>
      <Pill size="sm" variant={LIFECYCLE_VARIANT[workspaceState]}>
        <Pill.Text fontType="text">{LIFECYCLE_LABEL[workspaceState]}</Pill.Text>
      </Pill>
    </WorkspaceNestedLine>
  );
};

// Shows the workspace's profile level nested beneath the module's profile level pill, as a
// coloured pill on the same level→colour scale.
const WorkspaceProfileLevelLine: React.FC<{
  workspaceLevel: number | undefined;
}> = ({ workspaceLevel }) => {
  if (workspaceLevel === undefined) {
    return null;
  }
  return (
    <WorkspaceNestedLine>
      <Pill size="sm" variant={profileLevelVariant(workspaceLevel)}>
        <Pill.Text fontType="text">Level {workspaceLevel}</Pill.Text>
      </Pill>
    </WorkspaceNestedLine>
  );
};

export const ModuleKindSection: React.FC<Props> = ({
  modules,
  searchTerm,
  workspaceMap,
  orgMap,
  moduleKind,
  workspaces,
  agentWorkspaceIds,
  existingWorkspaceIds,
}) => {
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [versionFilter, setVersionFilter] = useState<VersionFilter>(null);
  const [prevSearchTerm, setPrevSearchTerm] = useState(searchTerm);
  if (searchTerm !== prevSearchTerm) {
    setPrevSearchTerm(searchTerm);
    setVersionFilter(null);
  }

  const {
    isOpen: isDiffOpen,
    openModal: openDiffModal,
    closeModal: closeDiffModal,
    afterLeave: afterDiffLeave,
    data: diffModule,
  } = useModal<Module>();

  const {
    isOpen: isEnableOpen,
    openModal: openEnableModal,
    closeModal: closeEnableModal,
    afterLeave: afterEnableLeave,
  } = useModal();

  const {
    isOpen: isCreateOpOpen,
    openModal: openCreateOpModal,
    closeModal: closeCreateOpModal,
    afterLeave: afterCreateOpLeave,
    data: createOpModule,
  } = useModal<Module>();

  const {
    isOpen: isOpsDetailOpen,
    openModal: openOpsDetailModal,
    closeModal: closeOpsDetailModal,
    afterLeave: afterOpsDetailLeave,
    data: opsDetailModule,
  } = useModal<Module>();

  const currentOpsDetailModule = opsDetailModule
    ? (modules.find((m) => m.id === opsDetailModule.id) ?? opsDetailModule)
    : undefined;

  // Re-read from the live list so the modal reflects queued operations (e.g. a
  // lifecycle change) rather than the snapshot captured when it was opened.
  const currentCreateOpModule = createOpModule
    ? (modules.find((m) => m.id === createOpModule.id) ?? createOpModule)
    : undefined;

  const {
    isOpen: isLifecycleOpen,
    openModal: openLifecycleModal,
    closeModal: closeLifecycleModal,
    afterLeave: afterLifecycleLeave,
    data: lifecycleModule,
  } = useModal<Module>();

  const createOperation = useCreateOperation();

  // Control sorting locally. The shared Table only manages sorting state when a parent owns it;
  // without these props header clicks are a no-op (it passes onSortingChange: undefined, which
  // overrides TanStack's built-in updater).
  const [sorting, setSorting] = useState<SortingState>([]);

  // The workspace's lifecycle state is the floor a module can't fall behind.
  const workspaceLifecycleById = useMemo(
    () => new Map(workspaces.map((ws) => [ws.id, ws.lifecycle_state] as const)),
    [workspaces],
  );
  const workspaceProfileLevelById = useMemo(
    () =>
      new Map(
        workspaces.map((ws) => [ws.id, ws.config?.profile_level ?? 0] as const),
      ),
    [workspaces],
  );

  // Re-read from the live list so the modal reflects the latest spec/desired.
  const currentLifecycleModule = lifecycleModule
    ? (modules.find((m) => m.id === lifecycleModule.id) ?? lifecycleModule)
    : undefined;

  const handleLifecycleTransition = async (target: LifecycleState) => {
    if (!currentLifecycleModule) return;
    try {
      await createOperation.mutateAsync({
        data: {
          type: "module_operations",
          attributes: { patch: { lifecycle_state: target } },
          relationships: {
            module: {
              data: { type: "modules", id: currentLifecycleModule.id },
            },
          },
        },
      });
      toastActions.success({
        title: "Lifecycle change queued",
        description: `Module ${currentLifecycleModule.id.slice(0, 8)} → ${LIFECYCLE_LABEL[target]}`,
      });
      closeLifecycleModal();
    } catch (error) {
      toastActions.failure({
        title: "Lifecycle change rejected",
        description: getApiErrorMessage(
          error,
          "Failed to change lifecycle state",
        ),
      });
    }
  };

  const searchableModules = useMemo(
    () => enrichModulesForSearch(modules, workspaceMap, orgMap),
    [modules, workspaceMap, orgMap],
  );
  const fuseSearch = useFuseSearch(searchableModules, MODULE_SEARCH_OPTIONS);
  const searchFilteredModules = useMemo(
    () => fuseSearch(searchTerm),
    [fuseSearch, searchTerm],
  );

  const filteredModules = useMemo(() => {
    if (!versionFilter) return searchFilteredModules;
    return searchFilteredModules.filter((mod) => {
      if (versionFilter.source === "not_deployed") {
        return mod.spec === null;
      }
      if (versionFilter.source === "desired") {
        return mod.desiredSpec.version === versionFilter.version;
      }
      return mod.spec?.version === versionFilter.version;
    });
  }, [searchFilteredModules, versionFilter]);

  // Auto-open ops modal when the search term just became a UUID matching one
  // visible op. Runs once per new searchTerm via prev-tracking so closing
  // the modal doesn't reopen it. Initialized to null (not searchTerm) so the
  // mount case also triggers when the page loads with ?search=<uuid>.
  const [prevAutoOpenSearch, setPrevAutoOpenSearch] = useState<string | null>(
    null,
  );
  if (searchTerm !== prevAutoOpenSearch) {
    setPrevAutoOpenSearch(searchTerm);
    if (searchTerm && UUID_RE.test(searchTerm)) {
      const needle = searchTerm.toLowerCase();
      const match = filteredModules.find(
        (mod) =>
          mod.activeOperations.some((op) => op.id === needle) ||
          mod.latestResolvedOperation?.id === needle,
      );
      if (match) {
        openOpsDetailModal(match);
      }
    }
  }

  const copilotQuotaByWorkspaceId = useMemo(
    () =>
      new Map(workspaces.map((ws) => [ws.id, isCaseCopilotQuotaEnabled(ws)])),
    [workspaces],
  );

  const allConfigKeys = useMemo(() => {
    const keysSet = new Set<string>();
    modules.map((mod) => {
      Object.keys((mod.spec ?? mod.desiredSpec).config).map((key) =>
        keysSet.add(key),
      );
      return null;
    });
    return [...keysSet].sort();
  }, [modules]);

  const getModuleContext = (mod: Module) => {
    const ws = workspaceMap.get(mod.workspaceId);
    return {
      workspaceName: ws?.name,
      orgName: ws ? orgMap.get(ws.orgId) : undefined,
    };
  };

  const opsDetailContext = currentOpsDetailModule
    ? getModuleContext(currentOpsDetailModule)
    : { workspaceName: undefined, orgName: undefined };

  const columns = useMemo(
    () => [
      columnHelper.accessor(
        (row) => {
          const ws = workspaceMap.get(row.workspaceId);
          const orgName = ws ? orgMap.get(ws.orgId) : undefined;
          return ws ? `${orgName ?? ""} ${ws.orgId}` : "";
        },
        {
          id: "organization",
          header: (info) => (
            <SortableFilterHeader info={info} showFilter>
              Organization
            </SortableFilterHeader>
          ),
          cell: (info) => {
            const mod = info.row.original;
            const ws = workspaceMap.get(mod.workspaceId);
            const orgName = ws ? orgMap.get(ws.orgId) : undefined;
            return (
              <Cell className="px-4" info={info}>
                {ws ? (
                  <span className="inline-flex items-center gap-1">
                    <Link
                      className="text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                      title={`Click to find organization ${orgName ?? ws.orgId} in the Workspaces tab`}
                      to={`/admin-panel/organizations?search=${ws.orgId}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="font-inter-normal-13px">
                        {orgName ?? ws.orgId.slice(0, 8)}
                      </span>
                    </Link>
                    <span onClick={(e) => e.stopPropagation()}>
                      <CopyTextIcon
                        feedback="inline"
                        size="xs"
                        tooltip="Copy organization ID"
                        value={ws.orgId}
                      />
                    </span>
                  </span>
                ) : (
                  <span className="font-inter-normal-13px text-gray-500">
                    —
                  </span>
                )}
              </Cell>
            );
          },
          enableSorting: true,
          enableColumnFilter: true,
          filterFn: (row, _columnId, filterValue: string) => {
            const mod = row.original;
            const ws = workspaceMap.get(mod.workspaceId);
            if (!ws) return false;
            const orgName = orgMap.get(ws.orgId) ?? "";
            const lower = filterValue.toLowerCase();
            return (
              orgName.toLowerCase().includes(lower) ||
              ws.orgId.toLowerCase().includes(lower)
            );
          },
          meta: { displayName: "Organization" },
        },
      ),
      columnHelper.accessor(
        (row) => {
          const ws = workspaceMap.get(row.workspaceId);
          return ws?.name ?? row.workspaceId;
        },
        {
          id: "workspace",
          header: (info) => (
            <SortableFilterHeader info={info} showFilter>
              Workspace
            </SortableFilterHeader>
          ),
          cell: (info) => {
            const mod = info.row.original;
            const ws = workspaceMap.get(mod.workspaceId);
            return (
              <Cell className="px-4" info={info}>
                <div className="flex items-center gap-1">
                  <div className="flex flex-col">
                    <Link
                      className="text-blue-600 transition-colors hover:text-blue-800 hover:underline"
                      title="Click to find this workspace in the Workspaces tab"
                      to={`/admin-panel/organizations?search=${mod.workspaceId}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="font-inter-normal-13px">
                        {ws?.name ?? mod.workspaceId.slice(0, 8)}
                      </span>
                    </Link>
                    {ws && (
                      <span className="font-inter-normal-12px text-gray-500">
                        {ws.slug}
                      </span>
                    )}
                  </div>
                  <span onClick={(e) => e.stopPropagation()}>
                    <CopyTextIcon
                      feedback="inline"
                      size="xs"
                      tooltip="Copy workspace ID"
                      value={mod.workspaceId}
                    />
                  </span>
                </div>
              </Cell>
            );
          },
          enableSorting: true,
          enableColumnFilter: true,
          filterFn: (row, _columnId, filterValue: string) => {
            const mod = row.original;
            const ws = workspaceMap.get(mod.workspaceId);
            const lower = filterValue.toLowerCase();
            return (
              (ws?.name.toLowerCase().includes(lower) ?? false) ||
              (ws?.slug.toLowerCase().includes(lower) ?? false) ||
              mod.workspaceId.toLowerCase().includes(lower)
            );
          },
          meta: { displayName: "Workspace" },
        },
      ),
      columnHelper.accessor("id", {
        id: "moduleId",
        header: (info) => (
          <SortableFilterHeader info={info} showFilter>
            Module ID
          </SortableFilterHeader>
        ),
        cell: (info) => (
          <Cell className="px-4" info={info}>
            <span className="inline-flex items-center gap-1">
              <span className="font-code-12 text-gray-900">
                {info.getValue().slice(0, 8)}
              </span>
              <span onClick={(e) => e.stopPropagation()}>
                <CopyTextIcon
                  feedback="inline"
                  size="xs"
                  tooltip="Copy module ID"
                  value={info.getValue()}
                />
              </span>
            </span>
          </Cell>
        ),
        // Ordering by an opaque UUID is meaningless, so the column is filter-only.
        enableSorting: false,
        enableColumnFilter: true,
        filterFn: "includesString",
        meta: { displayName: "Module ID" },
      }),
      columnHelper.accessor((row) => getModuleOperationStatus(row) ?? "", {
        id: "operationStatus",
        header: (info) => (
          <SortableFilterHeader
            filterOptions={OPERATION_STATUS_FILTER_OPTIONS}
            info={info}
          >
            Operation Status
          </SortableFilterHeader>
        ),
        cell: (info) => {
          const status = getModuleOperationStatus(info.row.original);
          if (!status) {
            return (
              <Cell className="px-4" info={info}>
                <span className="font-inter-normal-13px text-gray-400">—</span>
              </Cell>
            );
          }
          return (
            <Cell className="px-4" info={info}>
              <Pill variant={getOperationStatusPillVariant(status)}>
                <Pill.Text>{getOperationStatusLabel(status)}</Pill.Text>
              </Pill>
            </Cell>
          );
        },
        sortingFn: (rowA, rowB) => {
          const statuses = Object.values(ModuleOperationStatus);
          const getOrder = (mod: Module): number => {
            const status = getModuleOperationStatus(mod);
            return status ? statuses.indexOf(status) : statuses.length;
          };
          return getOrder(rowA.original) - getOrder(rowB.original);
        },
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row, _columnId, filterValue: string) =>
          getModuleOperationStatus(row.original) === filterValue,
        meta: { displayName: "Op Status" },
      }),
      columnHelper.accessor(
        (row) => (row.spec ?? row.desiredSpec).lifecycle_state,
        {
          id: "lifecycleState",
          header: (info) => (
            <SortableFilterHeader
              filterOptions={LIFECYCLE_FILTER_OPTIONS}
              info={info}
            >
              Lifecycle State
            </SortableFilterHeader>
          ),
          cell: (info) => {
            const current = info.getValue();
            const mod = info.row.original;
            const workspaceState = workspaceLifecycleById.get(mod.workspaceId);
            if (!current) {
              return (
                <Cell className="h-16 px-4" info={info}>
                  <div className="flex flex-col items-start gap-y-1">
                    <span className="font-inter-normal-13px text-gray-400">
                      —
                    </span>
                    <WorkspaceLifecycleLine workspaceState={workspaceState} />
                  </div>
                </Cell>
              );
            }
            const desired = mod.desiredSpec.lifecycle_state;
            const inFlight = desired !== undefined && current !== desired;
            const actions = computeAvailableActions({
              current,
              desired: desired ?? current,
              floor: workspaceLifecycleById.get(mod.workspaceId),
            });
            const actionable =
              actions.status === "ready" && actions.targets.length > 0;
            const pill = (
              <Pill variant={LIFECYCLE_VARIANT[current]}>
                {inFlight && <InFlightDot />}
                <Pill.Text fontType="text">
                  {LIFECYCLE_LABEL[current]}
                </Pill.Text>
              </Pill>
            );
            return (
              <Cell className="h-16 px-4" info={info}>
                <div className="flex flex-col items-start gap-y-1">
                  {actionable ? (
                    <button
                      className="inline-flex items-center gap-1 rounded-md px-1 py-0.5 hover:bg-gray-100"
                      title="Change lifecycle state"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openLifecycleModal(mod);
                      }}
                    >
                      {pill}
                      <Icon icon={faChevronDown} size="2xs" />
                    </button>
                  ) : inFlight && desired ? (
                    <Tooltip
                      asChild
                      title={`Desired: ${LIFECYCLE_LABEL[desired]}`}
                    >
                      {pill}
                    </Tooltip>
                  ) : (
                    pill
                  )}
                  <WorkspaceLifecycleLine workspaceState={workspaceState} />
                </div>
              </Cell>
            );
          },
          enableSorting: true,
          // Sort by lifecycle progression (Active → … → Deleted), not alphabetically.
          sortingFn: (rowA, rowB) => {
            const order = (mod: Module): number => {
              const state = (mod.spec ?? mod.desiredSpec).lifecycle_state;
              return state ? LIFECYCLE_ORDER.indexOf(state) : -1;
            };
            return order(rowA.original) - order(rowB.original);
          },
          enableColumnFilter: true,
          filterFn: (row, _columnId, filterValue: string) =>
            (row.original.spec ?? row.original.desiredSpec).lifecycle_state ===
            filterValue,
          meta: { displayName: "Lifecycle State" },
        },
      ),
      columnHelper.accessor(
        (row) => (row.spec ?? row.desiredSpec).profile_level,
        {
          id: "profileLevel",
          header: (info) => (
            <SortableFilterHeader info={info}>
              Profile Level
            </SortableFilterHeader>
          ),
          cell: (info) => {
            const level = info.getValue();
            const mod = info.row.original;
            const desiredLevel = mod.desiredSpec.profile_level;
            const inFlight = level !== desiredLevel;
            const pill = (
              <Pill variant={profileLevelVariant(level)}>
                {inFlight && <InFlightDot />}
                <Pill.Text fontType="text">Level {level}</Pill.Text>
              </Pill>
            );
            return (
              <Cell className="h-16 px-4" info={info}>
                <div className="flex flex-col items-start gap-y-1">
                  {inFlight ? (
                    <Tooltip asChild title={`Desired: Level ${desiredLevel}`}>
                      {pill}
                    </Tooltip>
                  ) : (
                    pill
                  )}
                  <WorkspaceProfileLevelLine
                    workspaceLevel={workspaceProfileLevelById.get(
                      mod.workspaceId,
                    )}
                  />
                </div>
              </Cell>
            );
          },
          enableSorting: true,
          enableColumnFilter: false,
          meta: { displayName: "Profile Level" },
        },
      ),
      columnHelper.display({
        id: "specStatus",
        header: (info) => (
          <SortableFilterHeader info={info}>Spec</SortableFilterHeader>
        ),
        cell: (info) => {
          const mod = info.row.original;
          const { variant, label } = getSpecStatus(mod);
          return (
            <Cell className="px-4" info={info}>
              <button
                className="cursor-pointer"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openDiffModal(mod);
                }}
              >
                <Pill variant={variant}>
                  <Pill.Text fontType="text">{label}</Pill.Text>
                </Pill>
              </button>
            </Cell>
          );
        },
        sortingFn: (rowA, rowB) => {
          const getOrder = (mod: Module) => {
            if (mod.spec === null) return 0;
            if (isEqual(mod.spec, mod.desiredSpec)) return 1;
            return 2;
          };
          return getOrder(rowA.original) - getOrder(rowB.original);
        },
        enableSorting: true,
        meta: { displayName: "Spec" },
      }),
      columnHelper.accessor((row) => (row.spec ?? row.desiredSpec).version, {
        id: "version",
        header: (info) => (
          <SortableFilterHeader info={info}>Version</SortableFilterHeader>
        ),
        cell: (info) => {
          const current = info.getValue();
          const mod = info.row.original;
          const desired = mod.desiredSpec.version;
          const inFlight = current !== desired;
          const content = (
            <span className="inline-flex items-center gap-1">
              {inFlight && <InFlightDot />}
              <code className="rounded bg-gray-200 px-2 py-1 font-code-12 text-gray-800">
                {current}
              </code>
              <span onClick={(e) => e.stopPropagation()}>
                <CopyTextIcon
                  feedback="inline"
                  size="xs"
                  tooltip="Copy version"
                  value={current}
                />
              </span>
            </span>
          );
          return (
            <Cell className="px-4" info={info}>
              {inFlight ? (
                <Tooltip asChild title={`Desired: ${desired}`}>
                  {content}
                </Tooltip>
              ) : (
                content
              )}
            </Cell>
          );
        },
        enableSorting: true,
        // Numeric collation so 1.10.0 sorts after 1.9.0 (not lexicographically).
        sortingFn: (rowA, rowB) =>
          (
            (rowA.original.spec ?? rowA.original.desiredSpec).version ?? ""
          ).localeCompare(
            (rowB.original.spec ?? rowB.original.desiredSpec).version ?? "",
            undefined,
            { numeric: true },
          ),
        enableColumnFilter: false,
        meta: { displayName: "Version" },
      }),
      ...(moduleKind === ModuleKind.CASE_MANAGEMENT
        ? [
            columnHelper.accessor(
              (row) => copilotQuotaByWorkspaceId.get(row.workspaceId) ?? false,
              {
                id: "copilotQuota",
                header: (info) => (
                  <SortableFilterHeader
                    filterOptions={COPILOT_QUOTA_FILTER_OPTIONS}
                    info={info}
                  >
                    Copilot Quota
                  </SortableFilterHeader>
                ),
                cell: (info) => (
                  <Cell className="px-4" info={info}>
                    <Pill variant={info.getValue() ? "green" : "gray"}>
                      <Pill.Text fontType="text">
                        {info.getValue() ? "Enabled" : "Disabled"}
                      </Pill.Text>
                    </Pill>
                  </Cell>
                ),
                enableSorting: true,
                enableColumnFilter: true,
                filterFn: (row, _columnId, filterValue: string) =>
                  (copilotQuotaByWorkspaceId.get(row.original.workspaceId) ??
                    false) ===
                  (filterValue === "enabled"),
                meta: { displayName: "Copilot Quota" },
              },
            ),
          ]
        : []),
      ...allConfigKeys.map((key) =>
        columnHelper.display({
          id: `config_${key}`,
          header: (info) => (
            <SortableFilterHeader info={info}>
              {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
            </SortableFilterHeader>
          ),
          cell: (info) => {
            const mod = info.row.original;
            const configValue = (mod.spec ?? mod.desiredSpec).config[key];
            return (
              <Cell className="px-4" info={info}>
                <ConfigValueCell value={configValue} />
              </Cell>
            );
          },
          enableSorting: false,
          meta: {
            displayName: key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase()),
          },
        }),
      ),
      columnHelper.display({
        id: "actions",
        header: () => <div className="w-10 px-4 py-3" />,
        cell: (info) => {
          const mod = info.row.original;
          return (
            <Cell className="px-4" info={info}>
              <Button
                size="sm"
                variant="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  openCreateOpModal(mod);
                }}
              >
                <Icon icon={faCog} size="sm" />
              </Button>
            </Cell>
          );
        },
        meta: { displayName: "Actions", sticky: "right" as const },
      }),
    ],
    [
      workspaceMap,
      orgMap,
      openDiffModal,
      openCreateOpModal,
      openLifecycleModal,
      workspaceLifecycleById,
      allConfigKeys,
      moduleKind,
      copilotQuotaByWorkspaceId,
    ],
  );

  const createOpContext = currentCreateOpModule
    ? getModuleContext(currentCreateOpModule)
    : { workspaceName: undefined, orgName: undefined };

  const lifecycleDesired = currentLifecycleModule?.desiredSpec.lifecycle_state;
  // Current (deployed) state; fall back to desired for not-yet-deployed modules.
  const lifecycleCurrent =
    currentLifecycleModule?.spec?.lifecycle_state ?? lifecycleDesired;
  const lifecycleContext = currentLifecycleModule
    ? getModuleContext(currentLifecycleModule)
    : { workspaceName: undefined, orgName: undefined };

  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    try {
      // Scoped to this kind; refetching all 5 kind caches is expensive.
      await queryClient.invalidateQueries({
        queryKey: moduleKeys.list({
          kind: moduleKind,
          include: "active_operations,latest_resolved_operation",
        }),
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <span className="font-inter-normal-13px text-gray-500">
          {filteredModules.length} module
          {filteredModules.length !== 1 ? "s" : ""} out of {workspaceMap.size}{" "}
          workspaces
        </span>
        <div className="flex items-center gap-2">
          <Button
            iconLeft={faRefresh}
            loading={isRefreshing}
            size="sm"
            variant="secondary"
            onClick={handleRefreshAll}
          >
            Refresh
          </Button>
          <Button
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={openEnableModal}
          >
            Enable Module
          </Button>
        </div>
      </div>
      {filteredModules.length === 0 ? (
        <div className="py-8 text-center font-inter-normal-13px text-gray-500">
          {searchTerm
            ? `No modules matching "${searchTerm}"`
            : "No modules of this kind"}
        </div>
      ) : (
        <>
          <ModuleVersionBars
            modules={searchFilteredModules}
            selectedFilter={versionFilter}
            onSelectFilter={setVersionFilter}
          />
          <div className="flex max-h-[calc(100vh-400px)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <TableComp
              clientSideFiltering
              columns={columns}
              data={filteredModules}
              getRowId={(row) => row.id}
              sorting={sorting}
              onSortingChange={setSorting}
              rowPropsGetter={(row) => ({
                onClick: () => openOpsDetailModal(row.original),
                classNameOverrides: twJoin(
                  "cursor-pointer",
                  searchTerm &&
                    moduleHasOperationMatch(row.original, searchTerm) &&
                    "bg-indigo-100",
                ),
              })}
              variant="default"
            />
            <SpecDiffModal
              afterLeave={afterDiffLeave}
              isOpen={isDiffOpen}
              module={diffModule}
              onClose={closeDiffModal}
            />
          </div>
        </>
      )}
      <EnableModuleModal
        afterLeave={afterEnableLeave}
        agentWorkspaceIds={agentWorkspaceIds}
        existingWorkspaceIds={existingWorkspaceIds}
        isOpen={isEnableOpen}
        moduleKind={moduleKind}
        orgMap={orgMap}
        workspaces={workspaces}
        onClose={closeEnableModal}
      />
      <CreateOperationModal
        afterLeave={afterCreateOpLeave}
        isOpen={isCreateOpOpen}
        module={currentCreateOpModule}
        orgName={createOpContext.orgName}
        workspaceName={createOpContext.workspaceName}
        onChangeLifecycle={() =>
          currentCreateOpModule && openLifecycleModal(currentCreateOpModule)
        }
        onClose={closeCreateOpModal}
      />
      {currentLifecycleModule && lifecycleCurrent && (
        <LifecycleTransitionModal
          afterLeave={afterLifecycleLeave}
          current={lifecycleCurrent}
          description={[
            lifecycleContext.orgName,
            lifecycleContext.workspaceName,
            currentLifecycleModule.kind,
          ]
            .filter(Boolean)
            .join(" · ")}
          desired={lifecycleDesired ?? lifecycleCurrent}
          floor={workspaceLifecycleById.get(currentLifecycleModule.workspaceId)}
          isOpen={isLifecycleOpen}
          isPending={createOperation.isPending}
          title="Change lifecycle state"
          onClose={closeLifecycleModal}
          onTransition={handleLifecycleTransition}
        />
      )}
      <Modal
        afterLeave={afterOpsDetailLeave}
        open={isOpsDetailOpen}
        size="lg"
        onClose={closeOpsDetailModal}
      >
        <Modal.Header
          description={
            currentOpsDetailModule
              ? [
                  opsDetailContext.orgName,
                  opsDetailContext.workspaceName,
                  currentOpsDetailModule.kind,
                ]
                  .filter(Boolean)
                  .join(" · ")
              : undefined
          }
        >
          Module Operations
        </Modal.Header>
        <Modal.Content>
          {currentOpsDetailModule && (
            <ModuleOperationsDetail
              module={currentOpsDetailModule}
              orgName={opsDetailContext.orgName}
              searchTerm={searchTerm}
              workspaceName={opsDetailContext.workspaceName}
              onChangeLifecycle={() =>
                openLifecycleModal(currentOpsDetailModule)
              }
              onCreateOperation={() =>
                openCreateOpModal(currentOpsDetailModule)
              }
            />
          )}
        </Modal.Content>
      </Modal>
    </div>
  );
};
